import {
  AttachmentFileBase,
  MessageContent,
  MessagesResponse as Response,
  Uri,
} from '@nextforce/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { useChatConfig } from 'chat/config/queries/get'
import { FileUploadStatus } from 'chat/send_message_context'
import keys from 'chat/shared/queries/keys'
import { appendFileToFormData } from 'chat/shared/util'
import { useChatWebsocketContext } from 'chat/websocket/context'
import { isNil } from 'lodash'
import Path from 'path'
import { Platform } from 'react-native'
import Url from 'url'
import { addMessagesDataToCache } from './shared/utils'

type Variables = {
  uri?: Uri
  content: MessageContent
  conversationId?: string
  setFileUploadStatus?: (status: FileUploadStatus) => void
}

export function useCreateMessage() {
  const { server } = useServer()
  const { data: chatConfig } = useChatConfig()
  const client = useQueryClient()
  const { sendWebsocketMessage } = useChatWebsocketContext()

  return useMutation({
    mutationFn: createMessage,
    onSuccess: async (event, variables) => {
      await addMessagesDataToCache(client, [event], chatConfig)
      sendWebsocketMessage({
        action: 'sendMessageEvent',
        messageEvent: event,
      })
      await client.invalidateQueries({
        queryKey: keys.readByCounts(String(event.conversation_id)),
      })
    },
  })

  async function createMessage({
    conversationId,
    content,
    uri,
    setFileUploadStatus,
  }: Variables) {
    return server.post<Response.AddMessageEvent>(
      uri ?? `/v2/chat/conversations/${conversationId}/messages`,
      await getMessageForm(content),
      'user',
      'multipart/form-data',
      progressEvent =>
        setFileUploadStatus &&
        setFileUploadStatus({
          progress: progressEvent.loaded / (progressEvent.total ?? 1),
        })
    )
  }
}

export async function getMessageForm(content: MessageContent) {
  const form = new FormData()

  if (content.attachment) {
    const attachment = content.attachment
    const formFieldName = 'attachment'
    const file: AttachmentFileBase = {
      name:
        attachment.name ??
        (Platform.OS !== 'web'
          ? Path.basename(Url.parse(attachment.uri).pathname ?? '')
          : undefined),
      uri: attachment.uri,
      mimeType: attachment.mimeType,
    }
    await appendFileToFormData(form, formFieldName, file)
  }
  form.append('body', content.body ?? '')
  if (!isNil(content.mentionedUserIds)) {
    form.append('mentioned_user_ids', JSON.stringify(content.mentionedUserIds))
  }

  if (!isNil(content.replyToMessageContent)) {
    form.append(
      'reply_to_message_json',
      JSON.stringify(content.replyToMessageContent)
    )
  }
  return form
}
